<template>
  <v-app-bar elevation="2" class="v-app-bar">
    <v-container class="d-flex align-center justify-space-between">
      <v-col cols="auto" class="navbar-brand pa-0">
        <v-btn
            variant="text"
            color="primary"
            class="name"
            @click="$router.push('/')"
            style="font-size: larger;"
        >
          <v-img
              v-if="logoUrl"
              :src="logoUrl"
              height="35"
              width="35"
              class="mr-2"
          />
          {{ websiteName }}
        </v-btn></v-col>

      <v-col
          cols="auto"
          class="navbar-actions pa-0 d-flex justify-end"
      >
        <v-btn
            class="navbar-action-btn"
            color="primary"
            aria-label="Unternehmen suchen"
            title="Unternehmen suchen"
            @click="openSearch"
        >
          <i class="fa-solid fa-magnifying-glass"/>
        </v-btn>
        <v-btn
            v-if="user?.type === 'company' && $route.path !== '/company'"
            class="navbar-action-btn"
            color="primary"
            aria-label="Unternehmensprofil"
            title="Unternehmensprofil"
            @click="handleAccount"
        >
          <i class="fa-regular fa-circle-user navbar-action-icon"/>
          <span class="navbar-action-label ml-2">Unternehmensprofil</span>
        </v-btn>
        <v-btn
            v-if="user"
            class="navbar-action-btn"
            color="primary"
            aria-label="Abmelden"
            title="Abmelden"
            @click="logout">
          <i class="fa-solid fa-arrow-right-from-bracket navbar-action-icon"/>
          <span class="navbar-action-label ml-2">Abmelden</span>
        </v-btn>
        <v-btn
            v-if="!user && $route.path !== '/company'"
            class="navbar-action-btn"
            color="primary"
            aria-label="Anmelden"
            title="Anmelden"
            @click="showModalLogin"
        >
          <i class="fa-solid fa-arrow-right-to-bracket navbar-action-icon"/>
          <span class="navbar-action-label ml-2">Anmelden</span>
        </v-btn>
      </v-col>
    </v-container>
  </v-app-bar>
  <v-overlay
      v-model="searchOverlay"
      class="align-start justify-center"
      scrim="rgba(0,0,0,.45)"
  >
    <v-card
        class="mt-16"
        width="600"
        max-width="calc(100vw - 32px)"
    >
      <v-card-title class="d-flex align-center justify-space-between">
        Unternehmen suchen
        <v-btn
            variant="text"
            @click="closeSearch"
        >
          <i class="fa-solid fa-xmark"/>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-text-field
            v-model="searchQuery"
            autofocus
            clearable
            hide-details
            label="Unternehmen suchen"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            @keydown.esc="closeSearch"
        />
      </v-card-text>
      <v-card-text class="pt-0">
        <v-list v-if="filteredListedFirmas.length">
          <v-list-item
              v-for="firma in filteredListedFirmas"
              :key="firma.strapiId || firma.id"
              @click="openFirma(firma)"
          >
            <template v-slot:prepend>
              <v-avatar size="42" rounded="lg">
                <v-img
                    v-if="firma.logo"
                    :src="firma.logo"
                    cover
                />
                <span v-else class="font-weight-bold">
                  {{ firma.companyName?.charAt(0) }}
                </span>
              </v-avatar>
            </template>
            <v-list-item-title class="font-weight-bold">
              {{ firma.companyName }}
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ getFirmaSubtitle(firma) }}
            </v-list-item-subtitle>
          </v-list-item>
        </v-list>
        <v-card
            v-else
            variant="outlined"
            class="text-center pa-5"
        >
          Keine gelisteten Unternehmen gefunden.
        </v-card>
      </v-card-text>
    </v-card>
  </v-overlay>
  <ModalLogin ref="ModalLogin"/>
  <ModalAccount ref="ModalAccount"/>
</template>

<script>
import {mapGetters} from "vuex";
import ModalAccount from "@/components/modal/Account";
import ModalLogin from "@/components/modal/Login";
import {getHomeSettings} from "@/services/siteSettings";

export default {
  name: 'AppNavbar',
  data() {
    return {
      searchOverlay: false,
      searchQuery: '',
      websiteName: 'MyService'
    }
  },
  components: {
    ModalAccount,
    ModalLogin

  },
  computed: {
    ...mapGetters([
      'user',
      'company',
      'firmas'
    ]),
    logoUrl() {
      return this.company?.logo || null;
    },
    listedFirmas() {
      return (this.firmas || []).filter(firma => firma?.serviceProfile?.gelistet === true)
    },
    filteredListedFirmas() {
      const query = String(this.searchQuery || '').trim().toLowerCase()

      if (!query) {
        return this.listedFirmas
      }

      return this.listedFirmas.filter(firma => {
        const haystack = [
          firma.companyName,
          firma.branche?.name,
          firma.place,
          firma.zip
        ].filter(Boolean).join(' ').toLowerCase()

        return haystack.includes(query)
      })
    }
  },
  methods: {
    async loadWebsiteName() {
      try {
        const response = await getHomeSettings()
        this.websiteName = response.data?.settings?.websiteName || 'MyService'
      } catch (error) {
        this.websiteName = 'MyService'
      }
    },
    openSearch() {
      this.searchOverlay = true

      if (!this.firmas?.length) {
        this.$store.dispatch('getFirmas')
      }
    },
    closeSearch() {
      this.searchOverlay = false
      this.searchQuery = ''
    },
    openFirma(firma) {
      const slug = this.createCompanySlug(firma.companyName)

      if (!slug) {
        return
      }

      this.closeSearch()
      this.$router.push(`/${slug}`)
    },
    getFirmaSubtitle(firma) {
      return [
        firma.branche?.name,
        [firma.zip, firma.place].filter(Boolean).join(' ')
      ].filter(Boolean).join(' - ')
    },
    createCompanySlug(value) {
      return String(value || '')
          .toLowerCase()
          .replace(/\u00e4/g, 'ae')
          .replace(/\u00f6/g, 'oe')
          .replace(/\u00fc/g, 'ue')
          .replace(/\u00df/g, 'ss')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]/g, '')
    },
    showModalLogin() {
      this.$refs.ModalLogin.setState(true);
    },

    showModalAccount() {
      this.$refs.ModalAccount.setState(true);
    },

    handleAccount() {

      // Unternehmen
      if (this.user?.type === 'company') {
        this.$router.push('/company');
        return;
      }

      // Normaler FE-User
      this.showModalAccount();
    },
    setState(state) {
      this.showModalAccount = state;
    },
    logout() {
      this.setState(false);
      this.$store.dispatch('logout');
      this.$router.push('/');
    }
  },
  mounted() {
    this.loadWebsiteName()
  },
  watch: {
    company() {
      this.$forceUpdate();
    }
  }
}
</script>
